import type { ReactElement } from "react";

import { useDragLayer } from "react-dnd";
import { createPortal } from "react-dom";

import type { SearchableSelectionOption } from "../../types";
import { SelectedColumnOptionView } from "../ColumnOptionView/ColumnOptionView";

import { StyledDragLayer, StyledDragLayerItem } from "./styles";

type DraggedColumn = {
    id: string;
    width: number;
};

type Props<O> = {
    findCol: (name: string) => { col: O; index: number };
};

const noop = () => undefined;

/**
 * Replaces the browser's native drag image, which is rasterised onto an opaque backdrop and so
 * cannot reproduce the card's rounded corners. Rendered into `document.body` because the modal is a
 * transformed ancestor, and a transform makes itself the containing block for `position: fixed`.
 */
export const ColumnOptionDragLayer = <O extends SearchableSelectionOption>({
    findCol,
}: Props<O>): ReactElement | null => {
    const { item, isDragging, sourceOffset } = useDragLayer((monitor) => ({
        item: monitor.getItem<DraggedColumn | null>(),
        isDragging: monitor.isDragging() && monitor.getItemType() === "card",
        sourceOffset: monitor.getSourceClientOffset(),
    }));

    if (!isDragging || !item || !sourceOffset) {
        return null;
    }

    const { col } = findCol(item.id);

    if (!col) {
        return null;
    }

    return createPortal(
        <StyledDragLayer data-automation="column-picker-drag-layer">
            <StyledDragLayerItem
                style={{
                    transform: `translate(${sourceOffset.x}px, ${sourceOffset.y}px)`,
                    width: item.width,
                }}
            >
                <SelectedColumnOptionView
                    {...col}
                    isDraggable
                    onClick={noop}
                />
            </StyledDragLayerItem>
        </StyledDragLayer>,
        document.body,
    );
};
